import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { businessDate, compareBusinessDates, systemClock } from '@sakhtar/calendar';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2DataGrid,
  V2EmptyState,
  V2FormActions,
  V2FormField,
  V2FormGrid,
  V2Modal,
  V2ToastStack,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import type { Reservation, ReservationListResponse } from '../../lib/types/inventory.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import {
  RESERVATION_STATUS_META,
  formatQuantity,
  metaOf,
  optionsOf,
  parseWholeQuantity,
  quantitySign,
  subtractQuantity,
} from './inventoryDisplay.js';
import { useToasts } from './useInventoryUi.js';
import './inventory.css';

const PICKER_LIMIT = 200;
const STATUS_FILTER_OPTIONS = [{ value: '', label: 'همه' }, ...optionsOf(RESERVATION_STATUS_META)];

/** The delivery box starts at the whole reserved quantity; a fractional reservation is typed by hand. */
const initialConsumeText = (r: Reservation): string =>
  r.quantity.denominator === '1' ? r.quantity.numerator : '';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Inventory.dc.html §07 RESERVATIONS · TRACKING · BARCODE
 * archetype: ENTITY_LIST + create form
 * status: KIT_CONFORMANT — real create/release/consume (real permission
 *   keys: inventory.reservation.create/.release, inventory.delivery.confirm
 *   — a genuinely different, fully-wired write path from receipt/issue/
 *   transfer/adjustment). The product/warehouse picker submits by real
 *   CODE, not by ID: `POST .../inventory/reservations` was extended this
 *   pass to accept `productCode`/`warehouseCode`/`locationCode` as an
 *   alternative to raw IDs (see apps/api/src/inventory/inventory.controller.ts),
 *   the same resolution `resolveMovementLine` already used for receipt/
 *   issue/transfer lines — `productId`/`warehouseId` from the read API
 *   are permanently Hidden by the master-data field-visibility policy's
 *   lowercase-only field-name constraint, so a picker built on those
 *   would always submit `undefined`.
 *
 *   Location is now a real picker too (BUG-11: `GET master-data/locations`
 *   exists), scoped to the selected warehouse, still submitted by code.
 *   The board's "سررسید" (reservation due-date) column doesn't exist — no
 *   expiry policy for reservations is contracted.
 * state reference: ready · empty · permission_denied · network_error
 */
export function ReservationsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { toasts, push, dismiss } = useToasts();
  const [statusFilter, setStatusFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [consumeTarget, setConsumeTarget] = useState<Reservation | null>(null);
  const [consumeQty, setConsumeQty] = useState('');
  const [consumeError, setConsumeError] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const [productCode, setProductCode] = useState('');
  const [warehouseCode, setWarehouseCode] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [quantityNumerator, setQuantityNumerator] = useState('');
  const [sourceReference, setSourceReference] = useState('');
  const [expiresAtDate, setExpiresAtDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [releasingExpired, setReleasingExpired] = useState(false);

  const reservations = useApiGet<ReservationListResponse>(
    slug ? `${base}/api/v1/inventory/reservations` : null,
    [slug, refreshKey],
  );
  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const warehouses = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/warehouses?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const locations = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/locations?status=active&limit=${PICKER_LIMIT}` : null,
    [slug],
  );

  const productOptions = products.status === 'ready' ? products.data.items : [];
  const warehouseOptions = warehouses.status === 'ready' ? warehouses.data.items : [];
  const locationOptions = locations.status === 'ready' ? locations.data.items : [];
  const locationOptionsForWarehouse = useMemo(() => {
    const warehouseId = warehouseOptions.find((w) => String(w.code) === warehouseCode)?.warehouseId;
    if (!warehouseId) return [];
    return locationOptions.filter((l) => String(l.warehouseId) === String(warehouseId));
  }, [locationOptions, warehouseOptions, warehouseCode]);

  if (!slug) return null;

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!productCode || !warehouseCode || !locationCode || !quantityNumerator || !sourceReference) {
      setError('همه فیلدهای ستاره‌دار الزامی است.');
      return;
    }
    const quantity = parseWholeQuantity(quantityNumerator);
    if (!quantity || quantitySign(quantity) <= 0) {
      setError('تعداد باید عدد صحیح و بزرگ‌تر از صفر باشد.');
      return;
    }
    setSubmitting(true);
    try {
      await apiPost(
        `${base}/api/v1/inventory/reservations`,
        {
          productCode,
          warehouseCode,
          locationCode,
          quantity,
          sourceModule: 'manual',
          sourceType: 'manual-reservation',
          sourceReference,
          expiresAt: expiresAtDate ? `${expiresAtDate}T23:59:59.999Z` : undefined,
        },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setProductCode('');
      setWarehouseCode('');
      setLocationCode('');
      setQuantityNumerator('');
      setSourceReference('');
      setExpiresAtDate('');
      setRefreshKey((k) => k + 1);
      push('رزرو ثبت شد.');
    } catch (err) {
      setError(bannerMessageFrom(err, 'ثبت رزرو انجام نشد.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onReleaseExpired() {
    setReleasingExpired(true);
    setPageError(null);
    try {
      const res = await apiPost<{ releasedCount: number }>(
        `${base}/api/v1/inventory/reservations/release-expired`,
        {},
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      push(`${res.releasedCount.toLocaleString('fa-IR')} رزرو منقضی‌شده آزاد شد.`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setPageError(bannerMessageFrom(err, 'آزادسازی رزروهای منقضی انجام نشد.'));
    } finally {
      setReleasingExpired(false);
    }
  }

  async function onRelease(reservationId: string) {
    setBusyId(reservationId);
    setPageError(null);
    try {
      await apiPost(
        `${base}/api/v1/inventory/reservations/release`,
        { reservationId },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      push('رزرو آزاد شد.');
    } catch (err) {
      setPageError(bannerMessageFrom(err, 'آزادسازی رزرو انجام نشد.'));
    } finally {
      setBusyId(null);
      setRefreshKey((k) => k + 1);
    }
  }

  async function submitConsume(event?: FormEvent) {
    if (event) event.preventDefault();
    if (!consumeTarget) return;
    const quantity = parseWholeQuantity(consumeQty);
    if (!quantity || quantitySign(quantity) <= 0) {
      setConsumeError('مقدار تحویل باید عدد صحیح و بزرگ‌تر از صفر باشد.');
      return;
    }
    const rest = subtractQuantity(consumeTarget.quantity, quantity);
    if (rest && quantitySign(rest) < 0) {
      setConsumeError(
        `مقدار تحویل نمی‌تواند بیشتر از مقدار رزرو (${formatQuantity(consumeTarget.quantity)}) باشد.`,
      );
      return;
    }
    setBusyId(consumeTarget.reservationId);
    setConsumeError(null);
    try {
      await apiPost(
        `${base}/api/v1/inventory/reservations/consume`,
        { reservationId: consumeTarget.reservationId, quantity },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setConsumeTarget(null);
      push('تحویل رزرو ثبت شد.');
    } catch (err) {
      setConsumeError(bannerMessageFrom(err, 'ثبت تحویل رزرو انجام نشد.'));
    } finally {
      setBusyId(null);
      setRefreshKey((k) => k + 1);
    }
  }

  const productSelectState =
    products.status === 'loading'
      ? { placeholder: 'در حال بارگذاری کالاها…', disabled: true }
      : productOptions.length === 0
        ? { placeholder: 'کالایی در اطلاعات پایه ثبت نشده', disabled: true }
        : { placeholder: '— انتخاب کالا —', disabled: false };
  const warehouseSelectState =
    warehouses.status === 'loading'
      ? { placeholder: 'در حال بارگذاری انبارها…', disabled: true }
      : warehouseOptions.length === 0
        ? { placeholder: 'انباری در اطلاعات پایه ثبت نشده', disabled: true }
        : { placeholder: '— انتخاب انبار —', disabled: false };
  const rows =
    reservations.status === 'ready'
      ? reservations.data.reservations.filter((r) => !statusFilter || r.status === statusFilter)
      : [];
  const consuming = consumeTarget !== null && busyId === consumeTarget.reservationId;

  return (
    <div className="inv-page">
      <V2Card>
        <V2CardHeader icon="plus" title="رزرو جدید" />
        <form className="inv-form inv-form--card" noValidate onSubmit={onCreate}>
          <V2FormGrid>
            <V2FormField label="کالا" required width="medium">
              <select
                className="sk-select"
                value={productCode}
                disabled={productSelectState.disabled}
                onChange={(e) => setProductCode(e.target.value)}
              >
                <option value="">{productSelectState.placeholder}</option>
                {productOptions.map((p) => (
                  <option key={String(p.code)} value={String(p.code)}>
                    {String(p.name ?? p.code)}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="انبار" required width="medium">
              <select
                className="sk-select"
                value={warehouseCode}
                disabled={warehouseSelectState.disabled}
                onChange={(e) => {
                  setWarehouseCode(e.target.value);
                  setLocationCode('');
                }}
              >
                <option value="">{warehouseSelectState.placeholder}</option>
                {warehouseOptions.map((w) => (
                  <option key={String(w.code)} value={String(w.code)}>
                    {String(w.name ?? w.code)}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="محل انبار" required width="short">
              <select
                className="sk-select"
                value={locationCode}
                disabled={!warehouseCode}
                title={!warehouseCode ? 'ابتدا انبار را انتخاب کنید' : undefined}
                onChange={(e) => setLocationCode(e.target.value)}
              >
                <option value="">
                  {!warehouseCode ? 'ابتدا انبار را انتخاب کنید' : '— انتخاب محل —'}
                </option>
                {locationOptionsForWarehouse.map((l) => (
                  <option key={String(l.code)} value={String(l.code)}>
                    {String(l.name ?? l.code)}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="تعداد" required width="short">
              <input
                className="sk-input ltr sk-num"
                inputMode="numeric"
                value={quantityNumerator}
                onChange={(e) => setQuantityNumerator(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="مرجع" required width="medium">
              <input
                className="sk-input"
                value={sourceReference}
                onChange={(e) => setSourceReference(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="تاریخ انقضا (اختیاری)" width="medium">
              <JalaliDateInput value={expiresAtDate} onChange={setExpiresAtDate} />
            </V2FormField>
          </V2FormGrid>

          {error ? <V2Alert tone="danger">{error}</V2Alert> : null}

          <V2FormActions>
            <V2Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'در حال ثبت…' : 'ثبت رزرو'}
            </V2Button>
          </V2FormActions>
        </form>
      </V2Card>

      {pageError ? (
        <V2Alert tone="danger" onClose={() => setPageError(null)}>
          {pageError}
        </V2Alert>
      ) : null}

      <div className="inv-toolbar-row">
        <span className="inv-form__lead">
          رزروهای دارای تاریخ انقضا پس از فرارسیدن موعد، قابل آزادسازی دسته‌جمعی هستند.
        </span>
        <V2Button
          variant="secondary"
          icon="check"
          disabled={releasingExpired}
          onClick={() => void onReleaseExpired()}
        >
          {releasingExpired ? 'در حال آزادسازی…' : 'آزادسازی رزروهای منقضی‌شده'}
        </V2Button>
      </div>

      {reservations.status === 'loading' ? <LoadingState /> : null}
      {reservations.status === 'error' ? (
        <StatePanel
          variant={reservations.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={reservations.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      {reservations.status === 'ready' ? (
        <V2DataGrid
          icon="layers"
          title="رزروها"
          subtitle={`${rows.length.toLocaleString('fa-IR')} رزرو`}
          filters={[
            {
              id: 'status',
              label: 'وضعیت',
              value: statusFilter,
              onChange: setStatusFilter,
              options: STATUS_FILTER_OPTIONS,
            },
          ]}
          columns={[
            {
              key: 'id',
              header: 'شمارهٔ رزرو',
              width: 120,
              render: (r: Reservation) => (
                <span className="ltr sk-num inv-code">{r.reservationId.slice(0, 8)}</span>
              ),
            },
            {
              key: 'product',
              header: 'کالا',
              width: 180,
              render: (r: Reservation) => (
                <span className="inv-cell">
                  <span className="inv-cell__main">
                    {r.productName ?? r.productCode ?? r.productId}
                  </span>
                  {r.productCode && r.productName ? (
                    <span className="inv-cell__sub ltr">{r.productCode}</span>
                  ) : null}
                </span>
              ),
            },
            {
              key: 'warehouse',
              header: 'انبار',
              width: 140,
              render: (r: Reservation) => (
                <span>{r.warehouseName ?? r.warehouseCode ?? r.warehouseId}</span>
              ),
            },
            {
              key: 'source',
              header: 'منبع / مرجع',
              width: 150,
              muted: true,
              render: (r: Reservation) => <span className="ltr">{r.sourceReference}</span>,
            },
            {
              key: 'quantity',
              header: 'مقدار',
              align: 'end',
              emphasis: 'key',
              width: 100,
              render: (r: Reservation) => (
                <span className="sk-num">{formatQuantity(r.quantity)}</span>
              ),
            },
            {
              key: 'expiresAt',
              header: 'سررسید انقضا',
              width: 140,
              render: (r: Reservation) => {
                if (!r.expiresAt) return <span>—</span>;
                const isExpired =
                  r.status === 'active' &&
                  compareBusinessDates(
                    businessDate(r.expiresAt.slice(0, 10)),
                    systemClock.today(),
                  ) < 0;
                return (
                  <span className="inv-actions">
                    <span className="sk-num">{displayJalaliDate(r.expiresAt)}</span>
                    {isExpired ? <V2Badge tone="danger">منقضی</V2Badge> : null}
                  </span>
                );
              },
            },
            {
              key: 'status',
              header: 'وضعیت',
              width: 110,
              render: (r: Reservation) => {
                const meta = metaOf(RESERVATION_STATUS_META, r.status);
                return <V2Badge tone={meta.tone}>{meta.label}</V2Badge>;
              },
            },
            {
              key: 'actions',
              header: 'عملیات',
              width: 200,
              hideable: false,
              render: (r: Reservation) =>
                r.status === 'active' ? (
                  <span className="inv-actions">
                    <V2Button
                      size="sm"
                      variant="secondary"
                      disabled={busyId === r.reservationId}
                      onClick={() => {
                        setConsumeTarget(r);
                        setConsumeQty(initialConsumeText(r));
                        setConsumeError(null);
                      }}
                    >
                      تحویل (مصرف)
                    </V2Button>
                    <V2Button
                      size="sm"
                      variant="ghost"
                      disabled={busyId === r.reservationId}
                      onClick={() => void onRelease(r.reservationId)}
                    >
                      آزادسازی
                    </V2Button>
                  </span>
                ) : null,
            },
          ]}
          rows={rows}
          rowKey={(r) => r.reservationId}
          rowTone={(r) => (r.status === 'active' ? 'info' : undefined)}
          legend={[{ tone: 'info', label: 'رزرو فعال' }]}
          legendNote="رزروهای فعال کالا را مقید نگه می‌دارند. در صورت فرارسیدن تاریخ انقضا، می‌توان رزروها را با دکمه آزادسازی سراسری آزاد کرد."
          emptyState={
            <V2EmptyState
              icon="info"
              title="رزروی ثبت نشده"
              description="رزرو جدید را از فرم بالا برای یک کالا و انبار ثبت کنید."
              compact
            />
          }
        />
      ) : null}

      <V2Modal
        open={consumeTarget !== null}
        onClose={() => setConsumeTarget(null)}
        dismissible={!consuming}
        size="sm"
        title="تأیید و تحویل رزرو (مصرف موجودی)"
        footer={
          <>
            <V2Button
              type="button"
              variant="ghost"
              onClick={() => setConsumeTarget(null)}
              disabled={consuming}
            >
              انصراف
            </V2Button>
            <V2Button
              type="submit"
              form="reservation-consume-form"
              variant="primary"
              disabled={consuming}
            >
              {consuming ? 'در حال ثبت…' : 'تأیید تحویل'}
            </V2Button>
          </>
        }
      >
        {consumeTarget ? (
          <form
            id="reservation-consume-form"
            className="inv-form"
            noValidate
            onSubmit={submitConsume}
          >
            <p className="inv-form__lead">
              کالا:{' '}
              <strong>
                {consumeTarget.productName ?? consumeTarget.productCode ?? consumeTarget.productId}
              </strong>
              <br />
              کل رزرو: <strong className="sk-num">{formatQuantity(consumeTarget.quantity)}</strong>
            </p>
            <V2FormField label="تعداد / مقدار تحویل" required width="full">
              <input
                className="sk-input ltr sk-num"
                inputMode="numeric"
                value={consumeQty}
                onChange={(e) => setConsumeQty(e.target.value)}
              />
            </V2FormField>
            {consumeError ? <V2Alert tone="danger">{consumeError}</V2Alert> : null}
          </form>
        ) : null}
      </V2Modal>

      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
